import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  CircleArrowLeft,
  FlaskConical,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';

import {
  useGetDemoModeStatus,
  useEnableDemoMode,
  useDisableDemoMode,
} from '~entities/demo-mode/api/demo-mode-api';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Switch } from '~shared/shadcn/ui/switch';

export const DemoModeSettings = () => {
  const navigate = useNavigate();
  const { data: status, isLoading } = useGetDemoModeStatus();
  const { data: studioDetails } = useGetStudioDetails();
  const enableMutation = useEnableDemoMode();
  const disableMutation = useDisableDemoMode();
  const [confirmAction, setConfirmAction] = useState<
    'enable' | 'disable' | null
  >(null);

  const isDemoMode = status?.isDemoMode ?? false;
  const isMutating = enableMutation.isPending || disableMutation.isPending;
  const parentPortalUrl =
    studioDetails?.locationId &&
    import.meta.env.VITE_PARENT_PORTAL_URL
      ? `${import.meta.env.VITE_PARENT_PORTAL_URL}/login?studioId=${studioDetails.locationId}`
      : null;

  const handleToggle = (checked: boolean) => {
    setConfirmAction(checked ? 'enable' : 'disable');
  };

  const handleConfirm = () => {
    if (confirmAction === 'enable') {
      enableMutation.mutate();
    } else if (confirmAction === 'disable') {
      disableMutation.mutate();
    }
    setConfirmAction(null);
  };

  return (
    <div className='mx-auto max-w-3xl space-y-6 p-6'>
      <div className='flex items-center gap-3'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => navigate({ to: '/dashboard' })}
        >
          <CircleArrowLeft className='h-5 w-5' />
        </Button>
        <h1 className='text-2xl font-semibold'>Demo Mode</h1>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : (
        <div className='space-y-6'>
          <div
            className={`rounded-lg p-6 transition-all duration-200 ${isDemoMode ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
          >
            <div className='flex items-start justify-between gap-4'>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <FlaskConical className='h-5 w-5 text-amber-600' />
                  <label className='text-lg font-medium'>
                    Demo Mode {isDemoMode ? '(Active)' : '(Inactive)'}
                  </label>
                </div>
                <p className='text-sm text-muted-foreground'>
                  When enabled, your studio is populated with sample data
                  (sessions, classes, events, students, and parents) and Stripe
                  is switched to a test sandbox. No live charges will be
                  processed while demo mode is active.
                </p>
                {isDemoMode && (
                  <p className='text-xs font-medium text-amber-600'>
                    Demo mode is active. Stripe is using test credentials. All
                    transactions are simulated.
                  </p>
                )}
              </div>
              <Switch
                checked={isDemoMode}
                onCheckedChange={handleToggle}
                disabled={isMutating}
              />
            </div>
          </div>

          {isDemoMode && status?.demoRecordCounts && (
            <div className='rounded-lg border bg-white p-6'>
              <h2 className='mb-4 text-base font-medium'>
                Demo Data Summary
              </h2>
              <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
                {Object.entries(status.demoRecordCounts).map(
                  ([key, count]) => (
                    <div key={key} className='text-center'>
                      <div className='text-2xl font-semibold'>{count}</div>
                      <div className='text-xs capitalize text-muted-foreground'>
                        {key}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {isDemoMode && status?.demoParentAccounts && (
            <div className='rounded-lg border bg-white p-6'>
              <h2 className='mb-4 text-base font-medium'>
                Demo Parent Login Credentials
              </h2>
              <p className='mb-4 text-sm text-muted-foreground'>
                Use these synthetic parent accounts in Parent Portal to test
                enrollments and payments. Do not use the studio admin account or
                legacy QA parents — they are not part of the demo dataset.
              </p>
              {parentPortalUrl ? (
                <p className='mb-4 text-sm'>
                  <a
                    href={parentPortalUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='font-medium text-blue-600 underline underline-offset-2'
                  >
                    Open Parent Portal
                  </a>
                </p>
              ) : null}
              <div className='space-y-3'>
                {status.demoParentAccounts.map((parent) => (
                  <div
                    key={parent.email}
                    className='rounded-md border bg-slate-50 p-3 text-sm'
                  >
                    <div className='font-medium'>{parent.name}</div>
                    <div>Email: {parent.email}</div>
                    <div>Password: {parent.password}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='rounded-lg border bg-white p-6'>
            <h2 className='mb-3 text-base font-medium'>How it works</h2>
            <ul className='space-y-2 text-sm text-muted-foreground'>
              <li>
                <strong>Enabling</strong> seeds your studio with realistic
                sample data (sessions, classes, events, parents, and students
                with medical info). Stripe credentials are swapped to a
                shared test account.
              </li>
              <li>
                <strong>While active</strong>, you can add, edit, and delete
                classes and students as normal. Demo seed data is tagged
                internally so it can be cleanly removed later.
              </li>
              <li>
                <strong>Disabling</strong> removes all demo-generated records
                and restores your original Stripe credentials. Any data you
                created yourself is not affected.
              </li>
            </ul>
          </div>

          <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
            <div className='flex gap-3'>
              <AlertTriangle className='h-5 w-5 shrink-0 text-amber-600' />
              <div className='text-sm text-amber-800'>
                <p className='font-medium'>Admin only</p>
                <p className='mt-1'>
                  Only studio administrators can enable or disable demo mode.
                  Demo parent accounts use synthetic data and cannot process
                  real payments.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'enable'
                ? 'Enable Demo Mode?'
                : 'Disable Demo Mode?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'enable'
                ? 'This will populate your studio with sample data and switch Stripe to a test sandbox. No live charges will be processed.'
                : 'This will remove all demo-generated data and restore your original Stripe credentials. Data you created yourself will not be affected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {confirmAction === 'enable' ? 'Enable' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
